/**
 * 员工、岗位与权限的后台接口（spec-identity D.2–D.4，AA-01–AA-36；`/admin-api/v1`）。
 * 页面只经这里调用接口；类型按规格写出，规格没有逐字段列出的响应（StaffRow、StaffDetail、/self/logs 的条目）
 * 取最小方案（D-P0-12-fe-02），字段名见本文件。
 *
 * 注意：员工登录类错误（STAFF_LOGIN_FAILED、ADMIN_TOTP_INVALID）的 HTTP 状态是 401，
 * 登录、解锁、修改本人密码三个请求传 `authRedirect: false`，避免输错密码被当作"会话失效"跳回登录页。
 */
import type { Lang, MaskFieldKind, MaskLevel, PermAction, RoleCode } from '@nexora/shared';
import { adminApi } from '../layout/api/client';
import type { StaffSession } from '../layout/session';

export type StaffStatus = 'pending' | 'active' | 'locked' | 'disabled';

/**
 * 后台敏感字段（spec-core MK1）：FULL 时为原值，其余级别为 `{ masked, revealable }`，HIDDEN 时字段被删除。
 * 登录 IP 与设备属 login_ip_device；A52 只有 R1 可看（完整），这里仍按两种形态处理。
 */
export type SensitiveText = string | { masked: string; revealable: boolean };
export type TokenPurpose = 'activation' | 'password_reset' | 'totp_reset';

/** staff_login_events.event（spec-identity B.3） */
export const STAFF_LOGIN_EVENTS = [
  'login_success',
  'login_failure',
  'login_locked',
  'ip_denied',
  'logout',
  'session_locked',
  'unlock_success',
  'unlock_failure',
  'session_revoked',
  'activation_completed',
  'password_reset_completed',
  'totp_rebound',
  'backup_code_used',
] as const;
export type StaffLoginEvent = (typeof STAFF_LOGIN_EVENTS)[number];

/** AA-04 StaffSelf */
export interface StaffSelf {
  staffNo: string;
  name: string;
  roles: RoleCode[];
  csNickname: string | null;
  csAvatarKey: string | null;
  serviceLangs: Lang[];
  onDuty: boolean;
  passwordRotateDue: boolean;
}

/** 员工密码规则中前端要显示的两个数（`system.staff_password_policy`；D-P0-12-fe-01） */
export interface PasswordPolicyView {
  minLen: number;
  history: number;
}

/** AA-04 完整响应（外壳只用其中一部分，见 layout/session.ts） */
export interface StaffSessionDetail extends StaffSession {
  staff: StaffSelf;
  passwordPolicy?: PasswordPolicyView;
}

export interface LastLogin {
  at: string;
  ip?: SensitiveText | null;
  device?: SensitiveText | null;
}

/** AA-14 列表行（REQ-5.17-005 的 13 个字段） */
export interface StaffRow {
  staffNo: string;
  name: string;
  workEmail: string;
  roles: RoleCode[];
  serviceLangs: Lang[];
  csNickname: string | null;
  csAvatarKey: string | null;
  isAdvisor: boolean;
  ownUid: string | null;
  testUid: string | null;
  status: StaffStatus;
  totpBound: boolean;
  ipWhitelistCount: number;
  lastLogin: LastLogin | null;
}

export interface IpEntry {
  cidr: string;
  note?: string | null;
}

export interface StaffSessionRow {
  sessionNo: string;
  createdAt: string;
  lastActivityAt: string;
  ip?: SensitiveText | null;
  device?: SensitiveText | null;
  locked: boolean;
}

export interface StaffLoginEventRow {
  at: string;
  event: StaffLoginEvent;
  ip?: SensitiveText | null;
  device?: SensitiveText | null;
}

/** AA-15 详情：列表字段 + 白名单条目、当前会话、最近 20 条登录事件、待生效链接 */
export interface StaffDetail extends StaffRow {
  applyGlobalWhitelist: boolean;
  ipWhitelist: IpEntry[];
  sessions: StaffSessionRow[];
  loginEvents: StaffLoginEventRow[];
  pendingToken: { purpose: TokenPurpose; expiresAt: string } | null;
}

export interface HeadcountBanner {
  r1Active: number;
  r6Active: number;
  min: number;
  r6OnDuty: number;
}

export interface StaffListResponse {
  items: StaffRow[];
  banner: HeadcountBanner;
  nextCursor?: string | null;
}

export interface StaffFilters {
  role?: RoleCode | '';
  status?: StaffStatus | '';
  lang?: Lang | '';
  totp?: 'bound' | 'unbound' | '';
  q?: string;
}

export interface StaffCreateBody {
  name: string;
  workEmail: string;
  roles: RoleCode[];
  serviceLangs: Lang[];
  csNickname?: string;
  csAvatarKey?: string;
  isAdvisor?: boolean;
  ownUid?: string;
  testUid?: string;
  ipWhitelist: IpEntry[];
  applyGlobalWhitelist: boolean;
  ackRoleConflict?: boolean;
}

export interface StaffPatchBody {
  name?: string;
  serviceLangs?: Lang[];
  csNickname?: string;
  csAvatarKey?: string;
  isAdvisor?: boolean;
  ownUid?: string | null;
  testUid?: string | null;
}

export type HandoverKind = 'kyc_review' | 'security_verification' | 'deposit_review' | 'withdraw_review' | 'cs_conversation' | 'review_initiated';

export interface HandoverItem {
  kind: HandoverKind;
  ref: string;
  title: string;
  since: string;
}

export type ModuleCapView = 'OP' | 'RO' | 'RV' | 'OP+RV' | 'NONE';

/** AA-30 岗位与权限点 */
export interface RolePermView {
  perm: string;
  menuGroup: string;
  page: string;
  actionClass: PermAction;
  default: boolean;
  effective: boolean;
  locked: boolean;
  overridable: boolean;
  moduleCap: ModuleCapView;
}

export interface RoleView {
  code: RoleCode;
  nameKey?: string;
  staffCount: number;
  perms: RolePermView[];
}

/** AA-33 字段级脱敏 */
export interface MaskCell {
  default: MaskLevel;
  effective: MaskLevel;
  allowedLevels: MaskLevel[];
}

export interface MaskFieldView {
  kind: MaskFieldKind;
  roles: Partial<Record<RoleCode, MaskCell>>;
}

export type PermChangeKind = 'role_assignment' | 'perm_override' | 'perm_reset' | 'mask_override' | 'mask_reset';

/** AA-36 权限变更记录 */
export interface PermChangeRow {
  id?: string;
  at: string;
  operator: { staffNo: string; name: string } | null;
  kind: PermChangeKind;
  target: string;
  before: unknown;
  after: unknown;
  note: string | null;
}

export interface SelfLoginLog {
  id?: string;
  at: string;
  event: StaffLoginEvent;
  ip?: SensitiveText | null;
  device?: SensitiveText | null;
}

export interface SelfOpsLog {
  id?: string;
  at: string;
  page: string | null;
  action: string;
  target: string | null;
  result: 'success' | 'failure';
}

export interface Paged<T> {
  items: T[];
  nextCursor?: string | null;
}

const enc = encodeURIComponent;
const noRedirect = { authRedirect: false } as const;

export const staffApi = {
  // ---- D.2 登录、激活与本人账户 ----
  /** AA-01 */
  login: (body: { workEmail: string; password: string; code: string }) =>
    adminApi.post<{ staff: StaffSelf; expiresAt: string }>('/auth/login', body, noRedirect),
  /** AA-03（锁屏中的会话） */
  unlock: (body: { password: string; code: string }) => adminApi.post<{ ok: true }>('/auth/unlock', body, noRedirect),
  /** AA-04（登录页用来判断是否已登录、是否在白名单内；不跳转） */
  probeSession: () => adminApi.get<StaffSessionDetail>('/auth/session', { passive: true, authRedirect: false }),
  /** AA-05 */
  tokenInfo: (token: string) =>
    adminApi.get<{
      purpose: TokenPurpose;
      staffName: string;
      workEmailMasked: string;
      steps: { password: boolean; totp: boolean; backupCodes: boolean };
      passwordPolicy?: PasswordPolicyView;
    }>(`/auth/tokens/${enc(token)}`, noRedirect),
  /** AA-06 */
  tokenPassword: (token: string, password: string) => adminApi.post<{ ok: true }>(`/auth/tokens/${enc(token)}/password`, { password }, noRedirect),
  /** AA-07 */
  totpSetup: (token: string) =>
    adminApi.post<{ secret: string; otpauthUri: string; qrDataUrl: string }>(`/auth/tokens/${enc(token)}/totp/setup`, {}, noRedirect),
  /** AA-08（备份码只返回这一次） */
  totpVerify: (token: string, code: string) =>
    adminApi.post<{ backupCodes: string[] }>(`/auth/tokens/${enc(token)}/totp/verify`, { code }, noRedirect),
  /** AA-09 */
  tokenComplete: (token: string) => adminApi.post<{ ok: true }>(`/auth/tokens/${enc(token)}/complete`, { backupCodesSaved: true }, noRedirect),
  /** AA-10（再次输入 TOTP） */
  changeOwnPassword: (body: { currentPassword: string; newPassword: string; totp: string }) =>
    adminApi.put<{ ok: true }>('/self/password', body, noRedirect),
  /** AA-11 */
  selfLogs: <T extends 'login' | 'ops'>(type: T, cursor?: string | null) =>
    adminApi.get<Paged<T extends 'login' ? SelfLoginLog : SelfOpsLog>>('/self/logs', { query: { type, cursor: cursor ?? undefined } }),
  /** AA-12（R7、R8） */
  setCsAvatar: (avatarKey: string) => adminApi.put<{ avatarKey: string }>('/self/cs-avatar', { avatarKey }),

  // ---- D.3 A52 员工管理 ----
  /** AA-14 */
  list: (filters: StaffFilters, cursor?: string | null, limit?: number) =>
    adminApi.get<StaffListResponse>('/staff', {
      query: {
        limit,
        role: filters.role || undefined,
        status: filters.status || undefined,
        lang: filters.lang || undefined,
        totp: filters.totp || undefined,
        q: filters.q?.trim() || undefined,
        cursor: cursor ?? undefined,
      },
    }),
  /** AA-15 */
  detail: (staffNo: string) => adminApi.get<StaffDetail>(`/staff/${enc(staffNo)}`),
  /** AA-16 */
  create: (body: StaffCreateBody) => adminApi.post<{ staffNo: string }>('/staff', body),
  /** AA-17 */
  update: (staffNo: string, body: StaffPatchBody) => adminApi.patch<StaffDetail>(`/staff/${enc(staffNo)}`, body),
  /** AA-18 */
  setRoles: (staffNo: string, roles: RoleCode[], ackRoleConflict?: boolean) =>
    adminApi.put<{ roles: RoleCode[]; rolesVersion: number }>(`/staff/${enc(staffNo)}/roles`, { roles, ...(ackRoleConflict ? { ackRoleConflict } : {}) }),
  /** AA-19 */
  setIpWhitelist: (staffNo: string, entries: IpEntry[], applyGlobalWhitelist: boolean) =>
    adminApi.put<{ entries: IpEntry[] }>(`/staff/${enc(staffNo)}/ip-whitelist`, { entries, applyGlobalWhitelist }),
  /** AA-20 */
  resendActivation: (staffNo: string) => adminApi.post<{ expiresAt: string }>(`/staff/${enc(staffNo)}/activation/resend`, {}),
  /** AA-21 */
  lock: (staffNo: string, reason: string) => adminApi.post<{ endedSessions: number }>(`/staff/${enc(staffNo)}/lock`, { reason }),
  /** AA-22 */
  unlockStaff: (staffNo: string, reason: string) => adminApi.post<{ ok: true }>(`/staff/${enc(staffNo)}/unlock`, { reason }),
  /** AA-23 */
  disable: (staffNo: string, reason: string) => adminApi.post<{ endedSessions: number }>(`/staff/${enc(staffNo)}/disable`, { reason }),
  /** AA-24 */
  resetPassword: (staffNo: string, reason?: string) =>
    adminApi.post<{ expiresAt: string }>(`/staff/${enc(staffNo)}/reset-password`, reason ? { reason } : {}),
  /** AA-25（原因必填） */
  reset2fa: (staffNo: string, reason: string) => adminApi.post<{ expiresAt: string }>(`/staff/${enc(staffNo)}/reset-2fa`, { reason }),
  /** AA-26 */
  forceLogout: (staffNo: string, reason: string) => adminApi.post<{ endedSessions: number }>(`/staff/${enc(staffNo)}/force-logout`, { reason }),
  /** AA-27 */
  handover: (staffNo: string) => adminApi.get<{ items: HandoverItem[] }>(`/staff/${enc(staffNo)}/handover`),
  /** AA-28 */
  transfer: (staffNo: string, transfers: { kind: HandoverKind; ref: string; toStaffNo: string }[]) =>
    adminApi.post<{ moved: number }>(`/staff/${enc(staffNo)}/handover`, { transfers }),

  // ---- D.4 A53 岗位与权限 ----
  /** AA-30 */
  roles: () => adminApi.get<{ roles: RoleView[] }>('/roles'),
  /** AA-31 */
  setRolePerms: (role: RoleCode, grant: string[], revoke: string[], note: string) =>
    adminApi.put<RoleView>(`/roles/${enc(role)}/permissions`, { grant, revoke, note }),
  /** AA-32 */
  resetRolePerms: (role: RoleCode, note: string) => adminApi.post<RoleView>(`/roles/${enc(role)}/permissions/reset`, { note }),
  /** AA-33 */
  maskRules: () => adminApi.get<{ fields: MaskFieldView[] }>('/mask-rules'),
  /** AA-34 */
  setMaskRules: (role: RoleCode, overrides: { kind: MaskFieldKind; level: MaskLevel }[], note: string) =>
    adminApi.put<{ fields: MaskFieldView[] }>(`/mask-rules/${enc(role)}`, { overrides, note }),
  /** AA-35 */
  resetMaskRules: (role: RoleCode, note: string) => adminApi.post<{ fields: MaskFieldView[] }>(`/mask-rules/${enc(role)}/reset`, { note }),
  /** AA-36 */
  permissionChanges: (filters: { kind?: PermChangeKind | ''; target?: string }, cursor?: string | null) =>
    adminApi.get<Paged<PermChangeRow>>('/permission-changes', {
      query: { kind: filters.kind || undefined, target: filters.target?.trim() || undefined, cursor: cursor ?? undefined },
    }),
} as const;

/** react-query 的 key（失效时按前缀） */
export const staffKeys = {
  list: (filters: StaffFilters) => ['staff', 'list', filters] as const,
  listAll: ['staff', 'list'] as const,
  detail: (staffNo: string) => ['staff', 'detail', staffNo] as const,
  handover: (staffNo: string) => ['staff', 'handover', staffNo] as const,
  roles: ['staff', 'roles'] as const,
  masks: ['staff', 'mask-rules'] as const,
  changes: (kind: string, target: string) => ['staff', 'permission-changes', kind, target] as const,
  selfLogs: (type: 'login' | 'ops') => ['staff', 'self-logs', type] as const,
};
